import React from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { defineMessages, useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";

import CardType from "@app/enum/dashboard/CardType";
import GoalType from "@app/enum/dashboard/GoalType";
import Section from "@app/scene/dashboard/components/addEditCard/components/EditCardForm/components/Section";
import ScopeTag from "@app/scene/dashboard/components/ScopeTag";
import canEditBoard from "@app/scene/dashboard/utils/canEditBoard";
import getGoalConfigurationProperties from "@app/scene/dashboard/utils/getGoalConfigurationProperties";
import useCard from "@app/scene/dashboard/utils/useCard";
import { getCurrentUser } from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { GoalConfiguration } from "@app/types/dashboard/CardConfiguration";
import Scope from "@app/types/dashboard/Scope";

import CustomTitle from "./CustomTitle";

const messages = defineMessages({
  goal: {
    id: "dashboard.editModal.configuration.goal",
    defaultMessage: "Goal",
    description: "Goal section title in Create/Edit Card modal on the Dashboard page",
  },
  title: {
    id: "dashboard.editModal.configuration.title",
    defaultMessage: "Title",
    description: "Custom title label in Create/Edit Card modal on the Dashboard page",
  },
});

interface ConfigurationProps {
  goalConfiguration?: GoalConfiguration;
  scope: Scope;
  teamGoal?: GoalConfiguration;
  type: CardType;
}

interface Props extends ConfigurationProps {
  currentUser: User;
  users: User[];
}

const Configuration: React.FC<Props> = ({
  currentUser,
  goalConfiguration,
  scope,
  teamGoal,
  type,
  users,
}) => {
  const intl = useIntl();
  const {
    CriteriaFormComponent,
    DateRangeFormComponent,
    getGoalType,
    GoalDetailsComponent,
    PreferencesFormComponent,
  } = useCard(type);

  const disabled = !canEditBoard(currentUser, scope, "editCardConfiguration");

  const goalConfigurationProps = getGoalConfigurationProperties(
    currentUser,
    scope,
    users,
    goalConfiguration,
    teamGoal
  );

  const { goalType } = getGoalType({ configuration: goalConfiguration });

  return (
    <>
      <Section>
        <Form.Item noStyle shouldUpdate>
          {() => (
            <Form.Item label={intl.formatMessage(messages.title)} name={["customTitle"]}>
              <CustomTitle disabled={disabled} scope={scope} type={type} />
            </Form.Item>
          )}
        </Form.Item>
      </Section>
      <Section title="Criteria">
        <CriteriaFormComponent disabled={disabled} scope={scope} />
      </Section>
      <Section title="Time Range">
        <DateRangeFormComponent disabled={disabled} scope={scope} />
      </Section>
      {PreferencesFormComponent && (
        <Section title="Preferences">
          <PreferencesFormComponent scope={scope} />
        </Section>
      )}
      {goalType !== GoalType.NO_GOAL && (
        <Form.Item name={["goal", "enabled"]} noStyle trigger="onToggle" valuePropName="checked">
          <Section
            title={
              <div>
                {intl.formatMessage(messages.goal)} <ScopeTag scope={scope} />
              </div>
            }
            toggleDisabled={!goalConfigurationProps.canToggle || disabled}
          >
            <GoalDetailsComponent
              currentUser={currentUser}
              goalConfiguration={goalConfiguration}
              goalConfigurationProps={goalConfigurationProps}
              goalType={goalType}
              scope={scope}
              teamGoal={teamGoal}
              type={type}
            />
          </Section>
        </Form.Item>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUser: getCurrentUser(state)!,
  users: getUsers(state),
});

export default connect(mapStateToProps)(Configuration);
